import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Eye, Clock, CheckCircle, XCircle, AlertCircle, FlaskConical, FileDown, MessageCircle } from 'lucide-react';
import { useLabProtocols, useLabPatients, useLabClinics, useCreateLabProtocol } from '@/hooks/useLab';
import { LabProtocolStatus } from '@/types';
import { LabProtocolCreate } from '@/services/lab';

const statusConfig: Record<LabProtocolStatus, { label: string; color: string; icon: React.ComponentType<{ className?: string }> }> = {
  pending: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  in_progress: { label: 'En Proceso', color: 'bg-lab-100 text-lab-800', icon: AlertCircle },
  completed: { label: 'Completado', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  cancelled: { label: 'Cancelado', color: 'bg-red-100 text-red-800', icon: XCircle },
};

export default function LabProtocols() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<LabProtocolStatus | ''>('');
  const [showModal, setShowModal] = useState(false);
  const [selectedClinicId, setSelectedClinicId] = useState<number | null>(null);
  const [formData, setFormData] = useState<LabProtocolCreate>({
    lab_patient_id: 0,
    requesting_vet_name: '',
    protocol_date: new Date().toISOString().split('T')[0],
    observations: '',
  });

  const { data: protocols = [], isLoading, refetch } = useLabProtocols({
    search: search || undefined,
    status: statusFilter || undefined,
  });
  const { data: clinics = [] } = useLabClinics({ is_active: true });
  const { data: allPatients = [] } = useLabPatients({ is_active: true });
  const createMutation = useCreateLabProtocol();

  // Filtrar pacientes por clínica seleccionada
  const filteredPatients = selectedClinicId
    ? allPatients.filter(p => p.lab_clinic_id === selectedClinicId)
    : allPatients;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.lab_patient_id) {
      alert('Seleccione un paciente');
      return;
    }
    try {
      const protocol = await createMutation.mutateAsync(formData);
      setShowModal(false);
      setSelectedClinicId(null);
      setFormData({
        lab_patient_id: 0,
        requesting_vet_name: '',
        protocol_date: new Date().toISOString().split('T')[0],
        observations: '',
      });
      refetch();
      navigate(`/lab/protocols/${protocol.id}`);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Protocolos</h1>
          <p className="text-gray-600">Gestionar protocolos de analisis</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-lab-600 text-white px-4 py-2 rounded-lg hover:bg-lab-700"
        >
          <Plus className="h-5 w-5" />
          Nuevo Protocolo
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por numero o paciente..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lab-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as LabProtocolStatus | '')}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lab-500"
          >
            <option value="">Todos los estados</option>
            <option value="pending">Pendiente</option>
            <option value="in_progress">En Proceso</option>
            <option value="completed">Completado</option>
            <option value="cancelled">Cancelado</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : protocols.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <FlaskConical className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No hay protocolos registrados</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Protocolo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paciente</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Veterinaria</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {protocols.map((protocol) => {
                const status = statusConfig[protocol.status];
                const StatusIcon = status.icon;
                return (
                  <tr key={protocol.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <span className="font-mono font-medium text-gray-900">{protocol.protocol_number}</span>
                    </td>
                    <td className="px-6 py-4">
                      {protocol.patient ? (
                        <div>
                          <div className="font-medium text-gray-900">{protocol.patient.name}</div>
                          <div className="text-sm text-gray-500 capitalize">{protocol.patient.species}</div>
                        </div>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4 text-gray-600">{formatDate(protocol.protocol_date)}</td>
                    <td className="px-6 py-4 text-gray-600">
                      {protocol.patient?.clinic?.name || protocol.requesting_vet_name || '-'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${status.color}`}>
                        <StatusIcon className="h-3.5 w-3.5" />
                        {status.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {protocol.status === 'completed' && (
                          <>
                            <button
                              onClick={(e) => { e.stopPropagation(); navigate(`/lab/protocols/${protocol.id}?action=pdf`); }}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                              title="Descargar PDF"
                            >
                              <FileDown className="h-4 w-4" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); navigate(`/lab/protocols/${protocol.id}?action=whatsapp`); }}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                              title="Enviar WhatsApp"
                            >
                              <MessageCircle className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => navigate(`/lab/protocols/${protocol.id}`)}
                          className="p-2 text-lab-600 hover:bg-lab-50 rounded-lg"
                          title="Ver detalle"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold">Nuevo Protocolo</h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {clinics.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Clínica</label>
                  <select
                    value={selectedClinicId || ''}
                    onChange={(e) => {
                      const clinicId = e.target.value ? parseInt(e.target.value) : null;
                      setSelectedClinicId(clinicId);
                      // Reset paciente cuando cambia la clínica
                      setFormData({ ...formData, lab_patient_id: 0 });
                    }}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-lab-500"
                  >
                    <option value="">Todas las clínicas</option>
                    {clinics.map((clinic) => (
                      <option key={clinic.id} value={clinic.id}>
                        {clinic.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Paciente *</label>
                <select
                  required
                  value={formData.lab_patient_id}
                  onChange={(e) => setFormData({ ...formData, lab_patient_id: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-lab-500"
                >
                  <option value={0}>Seleccionar paciente...</option>
                  {filteredPatients.map((patient) => (
                    <option key={patient.id} value={patient.id}>
                      {patient.name} - {patient.species} {patient.owner_name ? `(${patient.owner_name})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha *</label>
                <input
                  type="date"
                  required
                  value={formData.protocol_date}
                  onChange={(e) => setFormData({ ...formData, protocol_date: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-lab-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Veterinario solicitante</label>
                <input
                  type="text"
                  value={formData.requesting_vet_name}
                  onChange={(e) => setFormData({ ...formData, requesting_vet_name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-lab-500"
                  placeholder="Dr. ..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Observaciones</label>
                <textarea
                  value={formData.observations}
                  onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-lab-500"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 border rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="px-4 py-2 bg-lab-600 text-white rounded-lg hover:bg-lab-700 disabled:opacity-50"
                >
                  {createMutation.isPending ? 'Creando...' : 'Crear Protocolo'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
